/**
 * SharedSvgDefs
 *
 * Hidden SVG element containing all shared gradient/material definitions
 * used by the photo-real icon system. Mount once at app root (AppLayout)
 * so every icon can reference gradients via url(#gradient-id).
 *
 * IMPORTANT: gradient IDs here are global — never duplicate them elsewhere.
 */

export function SharedSvgDefs() {
  return (
    <svg
      style={{ position: "absolute", width: 0, height: 0 }}
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* ── Gold coin material ─────────────────────────────── */}
        <linearGradient id="pr-gold-face" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fde047" />
          <stop offset="35%" stopColor="#facc15" />
          <stop offset="70%" stopColor="#eab308" />
          <stop offset="100%" stopColor="#ca8a04" />
        </linearGradient>
        <linearGradient id="pr-gold-rim" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ca8a04" />
          <stop offset="100%" stopColor="#a16207" />
        </linearGradient>
        <radialGradient id="pr-gold-shine" cx="0.35" cy="0.3" r="0.5">
          <stop offset="0%" stopColor="#fff" stopOpacity={0.6} />
          <stop offset="100%" stopColor="#fff" stopOpacity={0} />
        </radialGradient>

        {/* ── Green paper money ──────────────────────────────── */}
        <linearGradient id="pr-bill-green" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#86efac" />
          <stop offset="40%" stopColor="#4ade80" />
          <stop offset="100%" stopColor="#16a34a" />
        </linearGradient>

        {/* ── Blue glass ─────────────────────────────────────── */}
        <linearGradient id="pr-glass-blue" x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0%" stopColor="#93c5fd" />
          <stop offset="50%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#1d4ed8" />
        </linearGradient>
        <radialGradient id="pr-glass-shine" cx="0.3" cy="0.25" r="0.4">
          <stop offset="0%" stopColor="#fff" stopOpacity={0.7} />
          <stop offset="100%" stopColor="#fff" stopOpacity={0} />
        </radialGradient>

        {/* ── Leather brown ──────────────────────────────────── */}
        <linearGradient id="pr-leather" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#d4a574" />
          <stop offset="50%" stopColor="#a0845c" />
          <stop offset="100%" stopColor="#78603c" />
        </linearGradient>
        <linearGradient id="pr-leather-dark" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#92704a" />
          <stop offset="100%" stopColor="#5c3d1e" />
        </linearGradient>

        {/* ── Paper / parchment ──────────────────────────────── */}
        <linearGradient id="pr-paper" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fefce8" />
          <stop offset="50%" stopColor="#fef9c3" />
          <stop offset="100%" stopColor="#fef08a" />
        </linearGradient>
        <linearGradient id="pr-paper-white" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#f1f5f9" />
        </linearGradient>

        {/* ── Metal / steel ──────────────────────────────────── */}
        <linearGradient id="pr-metal" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e2e8f0" />
          <stop offset="30%" stopColor="#cbd5e1" />
          <stop offset="70%" stopColor="#94a3b8" />
          <stop offset="100%" stopColor="#64748b" />
        </linearGradient>

        {/* ── Purple gem ─────────────────────────────────────── */}
        <linearGradient id="pr-gem-purple" x1="0" y1="0" x2="0.5" y2="1">
          <stop offset="0%" stopColor="#c4b5fd" />
          <stop offset="50%" stopColor="#8b5cf6" />
          <stop offset="100%" stopColor="#6d28d9" />
        </linearGradient>

        {/* ── Red seal wax ───────────────────────────────────── */}
        <linearGradient id="pr-wax-red" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fca5a5" />
          <stop offset="40%" stopColor="#ef4444" />
          <stop offset="100%" stopColor="#b91c1c" />
        </linearGradient>

        {/* ── Cherry wood ────────────────────────────────────── */}
        <linearGradient id="pr-cherry-wood" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#c2733a" />
          <stop offset="50%" stopColor="#9a5b2e" />
          <stop offset="100%" stopColor="#6d3a1a" />
        </linearGradient>

        {/* ── Green felt ─────────────────────────────────────── */}
        <linearGradient id="pr-felt-green" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#86efac" />
          <stop offset="100%" stopColor="#22c55e" />
        </linearGradient>

        {/* ── Amber glass ────────────────────────────────────── */}
        <linearGradient id="pr-glass-amber" x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0%" stopColor="#fde68a" />
          <stop offset="50%" stopColor="#f59e0b" />
          <stop offset="100%" stopColor="#b45309" />
        </linearGradient>

        {/* ── Teal ───────────────────────────────────────────── */}
        <linearGradient id="pr-teal-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#5eead4" />
          <stop offset="100%" stopColor="#0d9488" />
        </linearGradient>

        {/* ── Coral / warm orange ────────────────────────────── */}
        <linearGradient id="pr-coral" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fda4af" />
          <stop offset="50%" stopColor="#fb7185" />
          <stop offset="100%" stopColor="#e11d48" />
        </linearGradient>

        {/* ── Bronze ─────────────────────────────────────────── */}
        <linearGradient id="pr-bronze" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#d4a574" />
          <stop offset="50%" stopColor="#b8860b" />
          <stop offset="100%" stopColor="#8b6914" />
        </linearGradient>

        {/* ── Rose / HR accent ──────────────────────────────── */}
        <linearGradient id="pr-rose" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fda4af" />
          <stop offset="40%" stopColor="#f472b6" />
          <stop offset="100%" stopColor="#be185d" />
        </linearGradient>
        <linearGradient id="pr-rose-light" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fce7f3" />
          <stop offset="100%" stopColor="#fbcfe8" />
        </linearGradient>

        {/* ── Warm wood (lighter for furniture) ─────────────── */}
        <linearGradient id="pr-warm-wood" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#deb887" />
          <stop offset="50%" stopColor="#c19660" />
          <stop offset="100%" stopColor="#8b6c42" />
        </linearGradient>

        {/* ── Navy / dark blue ─────────────────────────────── */}
        <linearGradient id="pr-navy" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#93c5fd" />
          <stop offset="40%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#1e40af" />
        </linearGradient>

        {/* ── Sage / soft green ─────────────────────────────── */}
        <linearGradient id="pr-sage" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#bbf7d0" />
          <stop offset="100%" stopColor="#4ade80" />
        </linearGradient>
        {/* ── Violet / Ministry accent ─────────────────────── */}
        <linearGradient id="pr-violet" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#c4b5fd" />
          <stop offset="40%" stopColor="#8b5cf6" />
          <stop offset="100%" stopColor="#6d28d9" />
        </linearGradient>
        <linearGradient id="pr-violet-light" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ede9fe" />
          <stop offset="100%" stopColor="#ddd6fe" />
        </linearGradient>
        <linearGradient id="pr-violet-deep" x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0%" stopColor="#a78bfa" />
          <stop offset="50%" stopColor="#7c3aed" />
          <stop offset="100%" stopColor="#5b21b6" />
        </linearGradient>

        {/* ── Copper / Operations accent (#c06030) ────────── */}
        <linearGradient id="pr-copper" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f4a574" />
          <stop offset="40%" stopColor="#c06030" />
          <stop offset="100%" stopColor="#8b4020" />
        </linearGradient>
        <linearGradient id="pr-copper-light" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fdf0e8" />
          <stop offset="100%" stopColor="#fddccc" />
        </linearGradient>
        <linearGradient id="pr-copper-deep" x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0%" stopColor="#e8845a" />
          <stop offset="50%" stopColor="#c06030" />
          <stop offset="100%" stopColor="#7a3818" />
        </linearGradient>

        {/* ── Stained glass (warm multicolor) ─────────────── */}
        <linearGradient id="pr-stained-warm" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fde68a" />
          <stop offset="30%" stopColor="#f97316" />
          <stop offset="60%" stopColor="#ef4444" />
          <stop offset="100%" stopColor="#7c3aed" />
        </linearGradient>
        <linearGradient id="pr-stained-cool" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#93c5fd" />
          <stop offset="50%" stopColor="#818cf8" />
          <stop offset="100%" stopColor="#c084fc" />
        </linearGradient>

        {/* ── Teal / Calendar accent (#0e7c7b) ───────────── */}
        <linearGradient id="pr-cal-teal" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#5eead4" />
          <stop offset="40%" stopColor="#0e7c7b" />
          <stop offset="100%" stopColor="#065050" />
        </linearGradient>
        <linearGradient id="pr-cal-teal-light" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e6f5f5" />
          <stop offset="100%" stopColor="#ccebea" />
        </linearGradient>
        <linearGradient id="pr-cal-teal-deep" x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0%" stopColor="#2dd4bf" />
          <stop offset="50%" stopColor="#0e7c7b" />
          <stop offset="100%" stopColor="#044040" />
        </linearGradient>

        {/* ── Blue / Community accent (#2563a8) ──────────── */}
        <linearGradient id="pr-comm-blue" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#93c5fd" />
          <stop offset="40%" stopColor="#2563a8" />
          <stop offset="100%" stopColor="#1a4a80" />
        </linearGradient>
        <linearGradient id="pr-comm-blue-light" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#eaf2fb" />
          <stop offset="100%" stopColor="#d5e5f6" />
        </linearGradient>
        <linearGradient id="pr-comm-blue-deep" x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0%" stopColor="#60a5fa" />
          <stop offset="50%" stopColor="#2563a8" />
          <stop offset="100%" stopColor="#143870" />
        </linearGradient>

        {/* ── Cyan / Messaging accent (#0891b2) ──────────── */}
        <linearGradient id="pr-msg-cyan" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#67e8f9" />
          <stop offset="40%" stopColor="#0891b2" />
          <stop offset="100%" stopColor="#065a73" />
        </linearGradient>
        <linearGradient id="pr-msg-cyan-light" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e6f6fa" />
          <stop offset="100%" stopColor="#ccedf5" />
        </linearGradient>
        <linearGradient id="pr-msg-cyan-deep" x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0%" stopColor="#22d3ee" />
          <stop offset="50%" stopColor="#0891b2" />
          <stop offset="100%" stopColor="#044858" />
        </linearGradient>
      </defs>
    </svg>
  );
}
